import React, { useCallback, useState } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useProductGridRelatedProductsContext } from '~/grid/context/ProductGridRelatedProductsContext';
import ScrubsWaitlistModal from '~/shared/components/ScrubsWaitlistModal';
import { videoAspectRatio, breakpoints } from '~/shared/constants/productGrid';
import { SecondarySites } from '~/shared/constants/secondarySites';
import parseCustomVarAttributes from '~/shared/utils/parseCustomVarAttributes';
import usePromoTracking from '~/shared/utils/usePromoTracking';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { ImageAsset } from '~/techstyle-shared/react-assets';
import {
  Grid,
  PlaceholderGrid,
  Video,
  useGridState,
  GridColorSwatches,
  useProductGridPricingContext,
} from '~/techstyle-shared/react-components';
import { ProductType } from '~/techstyle-shared/react-products';

import { BreakpointsShape } from '../../constants/customPropTypes';
import ProductCell from '../ProductCell';
import ProductGridCarousel from '../ProductGridCarousel';

const { getSwatchData } = GridColorSwatches;

const AssetCell = ({ cellsWide, cellsTall, children, asset }) => {
  const { onPromoViewed, onPromoClicked } = usePromoTracking();
  const [isViewed, setIsViewed] = useState(false);

  const handleOnClickAsset = () => {
    onPromoClicked(asset);
  };

  const handleGridVisibility = (entry, unobserve) => {
    if (entry.isIntersecting) {
      onPromoViewed({ itemInfo: asset, id: asset.id });
      setIsViewed(true);
      unobserve();
    }
  };

  return (
    <Observer disabled={isViewed} onChange={handleGridVisibility}>
      <div
        role="link"
        tabIndex="0"
        style={{
          gridColumn: `span ${cellsWide}`,
          gridRow: `span ${cellsTall}`,
        }}
        onClick={handleOnClickAsset}
        onKeyDown={handleOnClickAsset}
      >
        {children}
      </div>
    </Observer>
  );
};

AssetCell.propTypes = {
  asset: PropTypes.object,
  cellsTall: PropTypes.string,
  cellsWide: PropTypes.string,
  children: PropTypes.node,
};

const FableticsPlaceholderGrid = styled(PlaceholderGrid)`
  --chinHeight: 114px;
  margin-bottom: var(--rowGap);
`;

const GridImageAsset = styled(ImageAsset).attrs(({ asset }) =>
  parseCustomVarAttributes(
    asset.options.customVars.clickable_asset_extra_properties
  )
)`
  cursor: pointer;
  width: 100%;
  picture img {
    border-radius: ${({ theme }) => theme.sizes.sm}px;
  }
`;

const GridWrapper = ({
  isCarouselGrid,
  breakpoint,
  breakpoints: customBreakpoints,
  children,
  ...rest
}) => {
  if (isCarouselGrid) {
    return (
      <ProductGridCarousel
        breakpoint={breakpoint}
        data-autotag="prodcarouselgrid"
      >
        {children}
      </ProductGridCarousel>
    );
  }
  return (
    <Grid
      style={{ marginBottom: breakpoint.rowGap || breakpoint.gap }}
      data-autotag="prodgrid"
      breakpoints={customBreakpoints || breakpoints}
      {...rest}
    >
      {children}
    </Grid>
  );
};

GridWrapper.propTypes = {
  breakpoint: PropTypes.object,
  breakpoints: BreakpointsShape,
  children: PropTypes.node,
  isCarouselGrid: PropTypes.bool,
};

export default function ProductGrid({
  boostedFplId,
  breakpoints,
  fplId,
  fplCode,
  items,
  imageAspectRatio,
  isCarouselGrid,
  payloadKey,
  placeholderProps,
  productImageType,
  productLinkOverride,
  productSource,
  startIndex,
  showColorsLink,
  showGridReviews,
  ...rest
}) {
  const { breakpoint } = useGridState();
  const { productGridRelatedProducts = {} } =
    useProductGridRelatedProductsContext() || {};

  const [isOpen, setModalOpen] = useState(false);
  const router = useRouter();
  const secondarySite = useSecondarySite(router.asPath);
  const { productGridPricing } = useProductGridPricingContext() || {};

  const openScrubsWaitlistModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeScrubsWaitlistModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  if (items) {
    return (
      <GridWrapper
        breakpoint={breakpoint}
        breakpoints={breakpoints}
        isCarouselGrid={isCarouselGrid}
        {...rest}
      >
        {items.map((item, index) => {
          if (item.itemType === 'asset') {
            const { customVars } = item.asset.options;
            let videoId = customVars.videoId || customVars.VideoID;
            // vimeo ids should be numbers, this is to prevent issues with bad data like vimeo:videoid# set in assets
            videoId = isNaN(videoId) ? null : videoId;
            let aspectRatio = videoAspectRatio;
            if (customVars.videoAspectRatio > 0) {
              aspectRatio = customVars.videoAspectRatio;
            }

            // temporary fake waitlist popup trigger
            const triggerFakeWaitlist = customVars?.showFakeWaitlist === true;

            return (
              <AssetCell
                asset={item.asset}
                key={`asset-${item.asset.id}`}
                cellsWide={item.cellsWide}
                cellsTall={item.cellsTall}
              >
                {videoId ? (
                  <Video
                    aspectRatio={aspectRatio}
                    url={Video.getUrlFromVideoId(videoId)}
                  />
                ) : (
                  <>
                    {triggerFakeWaitlist ? (
                      <GridImageAsset
                        asset={item.asset}
                        onClick={() => openScrubsWaitlistModal()}
                      />
                    ) : (
                      <GridImageAsset asset={item.asset} />
                    )}
                  </>
                )}
              </AssetCell>
            );
          }
          let fplIdList = item.featuredProductLocationIdList;

          if (typeof fplIdList === 'number') {
            fplIdList = [String(fplIdList)];
          }

          const isPinToTop =
            boostedFplId &&
            (typeof fplIdList === 'string' || Array.isArray(fplIdList))
              ? fplIdList.includes(boostedFplId)
              : false;
          const isBundle = item.productTypeId === ProductType.BUNDLE;

          // passing the swatch data from ProductGrid avoids recalculating SwatchData on every Re-render
          const itemMasterProductId = item?.masterProductId;

          if (
            !item.relatedProductIdObjectList &&
            productGridRelatedProducts[itemMasterProductId]
          ) {
            item = {
              ...item,
              ...productGridRelatedProducts[itemMasterProductId],
            };
          }

          const swatchData = !isBundle
            ? getSwatchData(item).filter((swatch) =>
                secondarySite === SecondarySites.OUTLET
                  ? swatch.isWarehouseProduct
                  : !swatch.isWarehouseProduct
              )
            : [];

          return (
            <GridColorSwatches.Context
              key={item.masterProductId}
              product={item}
            >
              <ProductCell
                product={item}
                productPricingFromGridContext={
                  productGridPricing?.[item?.masterProductId]
                }
                imageType={productImageType}
                imageAspectRatio={imageAspectRatio}
                gridIndex={startIndex + index}
                payloadKey={payloadKey}
                productSource={productSource}
                fplId={fplId}
                fplCode={fplCode}
                showColorsLink={showColorsLink}
                showGridReviews={showGridReviews}
                swatchData={swatchData}
                data-autotag={
                  isPinToTop
                    ? 'grid-product-pintotop'
                    : 'prodgrid-product-items'
                }
                href={productLinkOverride}
              />
            </GridColorSwatches.Context>
          );
        })}
        <ScrubsWaitlistModal
          isOpen={isOpen}
          onExit={() => closeScrubsWaitlistModal()}
        />
      </GridWrapper>
    );
  }
  return (
    <FableticsPlaceholderGrid
      imageAspectRatio={imageAspectRatio}
      data-autotag="empty-grid-message"
      {...placeholderProps}
    />
  );
}

ProductGrid.propTypes = {
  // Used for tracking.
  boostedFplId: PropTypes.number,
  // Custom breakpoints for grids
  breakpoints: BreakpointsShape,
  // The 'Featured Product' ID of the items shown in the grid. Used for tracking
  fplCode: PropTypes.string,
  // The 'Featured Product' ID of the items shown in the grid. Used for tracking.
  fplId: PropTypes.number,
  imageAspectRatio: PropTypes.number,
  // boolean that checks if the grid is a carousel grid
  isCarouselGrid: PropTypes.bool,
  // The items (products, image assets and video assets) to be displayed in the grid.
  // When this prop is null or undefined, the ProductGrid will instead show a
  // PlaceholderGrid.
  items: PropTypes.array,
  // A key based on the request that generated the current grid; used for tracking.
  payloadKey: PropTypes.string,
  // Props passed to the PlaceholderGrid, displayed when the `items` prop is null
  // or undefined.
  placeholderProps: PropTypes.object,
  // Tells each product whether to use the default image or, if available,
  // the plus-size image.
  productImageType: PropTypes.string,
  // override url for a product grid item, if this is set, after click on this product link in the grid, instead of going to the corresponding PDP, the user will land on the page of the override url.
  productLinkOverride: PropTypes.string,
  // How the product was accessed; used for tracking.
  productSource: PropTypes.string,
  // Determines wether to show link to more colors or not
  showColorsLink: PropTypes.bool,
  // Determines wether to show grid review stars or not
  showGridReviews: PropTypes.bool,
  // Used to calculate a ProductCell's position in the grid, which is
  // used for tracking.
  startIndex: PropTypes.number,
};
